import React from 'react';
import CustomSelect from '../../Common/CustomSelect';
import { getPaginationPageNumbers, normalizeCrmPagination } from './crmPaginationUtils';

const HeaderIcon = ({ icon }) => {
  if (icon === 'user') {
    return (
      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    );
  }
  if (icon === 'users') {
    return (
      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    );
  }
  return (
    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
    </svg>
  );
};

/**
 * Single sticky pagination bar for CRM list/card views (below filters, stays visible while scrolling).
 */
const CRMListPagination = ({
  pagination,
  entityLabel = 'items',
  loading = false,
  hasItems = true,
  onPageChange,
  onLimitChange,
  position = 'top',
  sticky = true,
  showLimitSelector,
  showWhenEmpty = false,
  icon = 'list',
  limitOptions = [10, 20, 50, 100],
}) => {
  const { page, limit, total, pages } = normalizeCrmPagination(pagination);
  const showLimit = showLimitSelector ?? position === 'top';

  if (total === 0 && !loading && !showWhenEmpty) return null;

  const borderClass = position === 'top' ? 'border-b' : 'border-t';
  const stickyClass = sticky && position === 'top' ? 'sticky top-0 z-20 shadow-sm' : '';
  const pageNumbers = getPaginationPageNumbers(page, pages, 7);
  const maxTopPages = Math.min(7, pages);

  return (
    <div className={`bg-gradient-to-r from-gray-100 to-gray-100 px-2 sm:px-4 lg:px-6 py-2 ${borderClass} border-gray-200 flex-shrink-0 ${stickyClass}`}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center">
            <HeaderIcon icon={icon} />
          </div>
          <div className="text-sm flex items-center gap-2">
            <div>
              {total > 0 ? (
                <>
                  <span className="font-semibold text-gray-900">
                    {((page - 1) * limit) + 1}-{Math.min(page * limit, total)}
                  </span>
                  <span className="text-gray-500 mx-1">of</span>
                  <span className="font-semibold text-gray-900">{total}</span>
                  <span className="text-gray-500 ml-1">{entityLabel}</span>
                </>
              ) : (
                <span className="text-gray-500">No {entityLabel}</span>
              )}
            </div>
            {loading && hasItems && (
              <svg className="animate-spin h-3 w-3 text-indigo-500" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            )}
          </div>
        </div>

        {pages > 0 && (
          <div className="flex items-center gap-3">
            {showLimit && onLimitChange && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 font-medium">Show:</span>
                <CustomSelect
                  value={limit}
                  onChange={(e) => onLimitChange(parseInt(e.target.value, 10))}
                  disabled={loading}
                  options={limitOptions.map((value) => ({ value, label: String(value) }))}
                  size="sm"
                  variant="minimal"
                  className="min-w-[70px]"
                />
              </div>
            )}

            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => onPageChange(1)}
                disabled={page <= 1 || loading}
                className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                title="First page"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => onPageChange(Math.max(1, page - 1))}
                disabled={page <= 1 || loading}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-sm"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="hidden sm:inline">Previous</span>
              </button>

              <div className="hidden md:flex items-center gap-1">
                {(position === 'bottom' ? pageNumbers : Array.from({ length: maxTopPages }, (_, i) => i + 1)).map((pageNum) => (
                  <button
                    key={pageNum}
                    type="button"
                    onClick={() => onPageChange(pageNum)}
                    disabled={loading}
                    className={`min-w-[32px] px-2.5 py-1.5 rounded text-sm font-medium transition-all ${
                      pageNum === page
                        ? position === 'bottom'
                          ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-md shadow-indigo-500/20'
                          : 'bg-indigo-600 text-white shadow-sm'
                        : 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                ))}
                {position === 'top' && pages > 7 && (
                  <span className="px-2 text-gray-400 text-sm">...</span>
                )}
              </div>

              <div className="flex md:hidden px-3 py-1.5 bg-white rounded border border-gray-200">
                <span className="text-sm font-medium text-gray-900">{page}</span>
                <span className="text-sm text-gray-500 mx-1">of</span>
                <span className="text-sm font-medium text-gray-900">{pages}</span>
              </div>

              <button
                type="button"
                onClick={() => onPageChange(Math.min(pages, page + 1))}
                disabled={page >= pages || loading}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-sm"
              >
                <span className="hidden sm:inline">Next</span>
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => onPageChange(pages)}
                disabled={page >= pages || loading}
                className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                title="Last page"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CRMListPagination;
